/**
 * O texto de "Compartilhar contato" (#595).
 *
 * O motor de conversa ainda não envia contato como cartão (vCard). Até isso
 * existir, o contato vai como uma mensagem de texto padronizada:
 *
 *     *Contato compartilhado:*
 *     João — Comercial
 *     *Telefone:* (11) 99999-9999
 *
 *     Para conversar pelo WhatsApp, clique no link:
 *     https://wa.me/5511999999999
 *
 * Os asteriscos são o negrito do WhatsApp, e é só lá que o botão aparece por
 * enquanto. A frase do link não diz "com ele" nem "com ela": o texto não sabe
 * o gênero de quem é compartilhado, e errar isso na frente do cliente é pior
 * que uma frase neutra.
 *
 * Quando o motor aceitar cartão, o botão e o modal ficam; muda só o que sai.
 */

export interface DadosDoContato {
  readonly nome: string | null;
  readonly cargo: string | null;
  /** E.164, com o `+`. */
  readonly telefone: string;
}

/**
 * O telefone como se escreve no Brasil: `(11) 99999-9999`, ou `(11) 3333-4444`
 * sem o nono dígito.
 *
 * Só formata o que reconhece: DDI 55, DDD e oito ou nove dígitos. Número de
 * fora sai como veio, com o `+`: formatar à brasileira um número estrangeiro
 * produziria um telefone que não existe.
 */
export function telefoneParaExibir(e164: string): string {
  const casou = /^\+55(\d{2})(\d{4,5})(\d{4})$/.exec(e164);
  if (!casou) return e164;
  return `(${casou[1]}) ${casou[2]}-${casou[3]}`;
}

/** `https://wa.me/<número>`: DDI, DDD e número, sem o `+` e sem pontuação. */
export function linkDoWhatsApp(e164: string): string {
  return `https://wa.me/${e164.replace(/\D/g, '')}`;
}

/** A mensagem inteira, pronta para entrar no campo do editor. */
export function mensagemDeContato(contato: DadosDoContato): string {
  const nome = contato.nome?.trim() ?? '';
  const cargo = contato.cargo?.trim() ?? '';
  // Sem cargo, só o nome, sem o travessão pendurado. Sem nome, o cargo sozinho
  // não identifica ninguém, e a linha sai.
  const quem = nome && cargo ? `${nome} — ${cargo}` : nome;

  return [
    '*Contato compartilhado:*',
    ...(quem ? [quem] : []),
    `*Telefone:* ${telefoneParaExibir(contato.telefone)}`,
    '',
    'Para conversar pelo WhatsApp, clique no link:',
    linkDoWhatsApp(contato.telefone),
  ].join('\n');
}
